<script lang="ts">
    let id = 0;
</script>
<script setup lang="ts">
    import { Field, FieldLabel, FieldDescription, FieldError } from '../../../ui/Field';
    import { InputGroup, InputGroupInput, InputGroupAddon } from '../../../ui/InputGroup';
    import { Spinner } from '@/shared/ui/Spinner';
    import { TooltipButton } from '../../../ui/TooltipButton';
    import { RuleExpression, useField, useFieldError } from 'vee-validate';
    import { required } from '@vee-validate/rules';
    import { Collapsible, CollapsibleTrigger } from '@/shared/ui/Collapsible';
    import { FieldHint } from '@/shared/ui/FieldHint';

    const props = withDefaults(
        defineProps<{
            name?: string;
            label?: string;
            type?: 'text' | 'number' | 'tel' | 'password' | 'email' | 'date';
            placeholder?: string;
            description?: string;
            loading?: boolean;
            tooltip?: string;
            /** Rules will be ignored if you use schema based validation */
            rules?: MaybeRef<RuleExpression<any>>;
            disabled?: boolean;
            hideError?: boolean;
            maxlength?: number;
        }>(),
        {
            name: () => `input_field_${++id}`,
        },
    );

    const emit = defineEmits<{ blur: [] }>();

    const model = defineModel<string | number>();

    const { value: localModel, meta, handleBlur, handleChange } = useField(props.name, toRef(props, 'rules' as any), { initialValue: model.value });
    const error = useFieldError(props.name);

    function onBlur(event: Event) {
        handleBlur(event);
        emit('blur');
    }

    const isInvalid = computed(() => meta.touched && !!error.value);

    const isRequired = computed(() => {
        if (typeof props.rules === 'string') {
            return !!props.rules.includes('required');
        }

        if (typeof props.rules === 'object') {
            return Object.values(props.rules).some((validator) => validator === required);
        }

        if (Array.isArray(props.rules)) {
            return props.rules.some((validator) => validator === required);
        }

        return false;
    });

    watch(localModel, (value) => {
        if (props.type === 'number') {
            model.value = value === '' ? 0 : Number(value);
        } else {
            model.value = value;
        }
    });

    watch(model, (value) => {
        if (props.type === 'number') {
            localModel.value = value ?? 0;
        } else {
            localModel.value = value ?? '';
        }
    });
</script>

<template>
    <Collapsible>
        <Field>
            <div
                v-if="label || $slots.label"
                class="flex gap-1 items-center"
            >
                <FieldLabel>
                    <slot name="label">{{ label }}</slot>
                </FieldLabel>
                <CollapsibleTrigger as-child>
                    <TooltipButton v-if="$slots.tooltip || tooltip"></TooltipButton>
                </CollapsibleTrigger>
            </div>

            <FieldHint v-if="$slots.tooltip || tooltip">
                <slot name="tooltip">{{ tooltip }}</slot>
            </FieldHint>

            <InputGroup>
                <InputGroupAddon v-if="$slots.addonBefore"><slot name="addonBefore" /></InputGroupAddon>
                <InputGroupInput
                    v-model="localModel"
                    :name="name"
                    :placeholder="placeholder"
                    :disabled="disabled || loading"
                    :aria-invalid="isInvalid"
                    :aria-required="isRequired"
                    :type="type"
                    :maxlength="maxlength"
                    @change="handleChange"
                    @blur="onBlur"
                />
                <InputGroupAddon
                    v-if="$slots.addonAfter || loading"
                    align="inline-end"
                >
                    <Spinner v-if="loading" />
                    <slot name="addonAfter" />
                </InputGroupAddon>
            </InputGroup>

            <FieldDescription v-if="description || $slots.description">
                <slot name="description">{{ description }}</slot>
            </FieldDescription>

            <FieldError v-if="isInvalid && !hideError">{{ error }}</FieldError>
        </Field>
    </Collapsible>
</template>
